import React from "react";
import trustBudMark from "../../assets/logo/trustbud-mark.png";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import BarChartIcon from "@mui/icons-material/BarChart";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { apiFetch } from "../../api/apiFetch";
import { fetchActiveSprint, getSprintWorkspacePath } from "../../api/trustbuds";
import { DESTINATIONS } from "../../navigation/destinationLabels";
import FeedbackDialog from "./FeedbackDialog";

const ACTIVITY_LAST_SEEN_KEY = "trustbud.activity.lastSeenAt";

function TopBar({ onLogout, username }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [hasUnreadTimeline, setHasUnreadTimeline] = React.useState(false);
  const [hasIncomingRequests, setHasIncomingRequests] = React.useState(false);
  const [profileImage, setProfileImage] = React.useState("");
  const [activeSprint, setActiveSprint] = React.useState(null);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const open = Boolean(anchorEl);

  function handleOpenMenu(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setAnchorEl(null);
  }

  function handleLogout() {
    handleCloseMenu();
    onLogout?.();
  }

  function handleNavigate(path) {
    handleCloseMenu();
    if (path === DESTINATIONS.updates.path) {
      markActivitySeen();
    }
    navigate(path);
  }

  function handleTrustBudNavigate(fallbackPath) {
    handleCloseMenu();
    if (activeSprint?.id) {
      navigate(
        getSprintWorkspacePath({
          sprintId: activeSprint.id,
          connectionId: activeSprint?.network_connection_id,
          userId: activeSprint?.other_user?.id,
        })
      );
      return;
    }
    navigate(fallbackPath);
  }

  function handleInvite() {
    handleCloseMenu();
    window.dispatchEvent(new Event("trustbud:open-invite"));
  }

  function handleOpenFeedback() {
    handleCloseMenu();
    setFeedbackOpen(true);
  }

  React.useEffect(() => {
    function handleUnread(event) {
      setHasUnreadTimeline(Boolean(event.detail?.hasUnread));
    }
    window.addEventListener("trustbud:timeline-unread", handleUnread);
    return () => window.removeEventListener("trustbud:timeline-unread", handleUnread);
  }, []);

  const markActivitySeen = React.useCallback(async () => {
    const seenAt = window.__trustbudLatestActivityAt || new Date().toISOString();
    localStorage.setItem(ACTIVITY_LAST_SEEN_KEY, seenAt);
    setHasUnreadTimeline(false);
    window.dispatchEvent(new CustomEvent("trustbud:timeline-unread", { detail: { hasUnread: false } }));
    try {
      const state = await apiFetch("/activity/seen/", {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (state?.latest_activity_at) {
        window.__trustbudLatestActivityAt = state.latest_activity_at;
      }
      if (state?.last_seen_at) {
        localStorage.setItem(ACTIVITY_LAST_SEEN_KEY, state.last_seen_at);
      }
    } catch {
      // Local read state keeps the badge quiet until the next successful poll.
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    async function loadActivityUnread(forceSeen = false) {
      try {
        const [threads, activityState] = await Promise.all([
          apiFetch("/activity/chats/"),
          apiFetch("/activity/state/"),
        ]);
        if (cancelled) return;
        const hasUnreadMessages = Array.isArray(threads)
          ? threads.some((thread) => Number(thread?.unreadCount || 0) > 0)
          : false;
        const latestActivityAt = String(activityState?.latest_activity_at || "");
        if (latestActivityAt) {
          window.__trustbudLatestActivityAt = latestActivityAt;
        }
        if (activityState?.last_seen_at) {
          localStorage.setItem(ACTIVITY_LAST_SEEN_KEY, activityState.last_seen_at);
        }
        if (forceSeen) {
          await markActivitySeen();
          return;
        }
        const nextHasUnread = Boolean(
          hasUnreadMessages ||
            activityState?.has_unread ||
            Number(activityState?.unread_count || 0) > 0 ||
            Number(activityState?.unread_needs_response_count || 0) > 0
        );
        setHasUnreadTimeline(nextHasUnread);
        window.dispatchEvent(new CustomEvent("trustbud:timeline-unread", { detail: { hasUnread: nextHasUnread } }));
      } catch {
        // Keep prior badge state if the check fails.
      }
    }

    loadActivityUnread(window.location.pathname.startsWith("/activity"));
    intervalId = window.setInterval(() => loadActivityUnread(false), 120000);
    const handler = () => loadActivityUnread(false);
    const seenHandler = () => loadActivityUnread(true);
    window.addEventListener("trustbud:connections-changed", handler);
    window.addEventListener("trustbud:activity-refresh", handler);
    window.addEventListener("trustbud:activity-seen", seenHandler);
    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener("trustbud:connections-changed", handler);
      window.removeEventListener("trustbud:activity-refresh", handler);
      window.removeEventListener("trustbud:activity-seen", seenHandler);
    };
  }, [markActivitySeen]);

  React.useEffect(() => {
    let isMounted = true;
    async function loadProfile() {
      try {
        const data = await apiFetch("/account-settings/");
        if (!isMounted) return;
        setProfileImage(data?.profile_image || "");
      } catch {
        // ignore
      }
    }
    loadProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    async function loadActiveSprint() {
      try {
        const sprint = await fetchActiveSprint();
        if (isMounted) setActiveSprint(sprint);
      } catch {
        if (isMounted) setActiveSprint(null);
      }
    }
    loadActiveSprint();
    const handler = () => loadActiveSprint();
    window.addEventListener("trustbud:connections-changed", handler);
    return () => {
      isMounted = false;
      window.removeEventListener("trustbud:connections-changed", handler);
    };
  }, []);

  React.useEffect(() => {
    async function loadRequests() {
      try {
        const data = await apiFetch("/companions/requests/");
        const incoming = Array.isArray(data?.incoming) ? data.incoming : [];
        setHasIncomingRequests(incoming.length > 0);
        window.dispatchEvent(
          new CustomEvent("trustbud:requests-unread", {
            detail: { count: incoming.length },
          })
        );
      } catch {
        setHasIncomingRequests(false);
        window.dispatchEvent(
          new CustomEvent("trustbud:requests-unread", {
            detail: { count: 0 },
          })
        );
      }
    }

    function handleRequests(event) {
      setHasIncomingRequests(Boolean(event.detail?.count));
    }

    loadRequests();
    window.addEventListener("trustbud:requests-unread", handleRequests);
    return () =>
      window.removeEventListener("trustbud:requests-unread", handleRequests);
  }, []);

  return (
    <>
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderBottom: "1px solid",
        borderColor: "rgba(15, 23, 42, 0.06)",
        bgcolor: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(16px)",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: { xs: 60, sm: 72 }, px: { xs: 1.5, sm: 3 } }}>
        {/* Left side: App name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          <Box
            component="img"
            src={trustBudMark}
            alt="TrustBud"
            sx={{
              width: { xs: 29, sm: 34 },
              height: { xs: 24, sm: 29 },
            }}
          />

          <Typography variant="h6" fontWeight={850} color="text.primary" sx={{ fontSize: { xs: "1rem", sm: "1.16rem" }, letterSpacing: 0 }}>
            TrustBud
          </Typography>
        </Box>

        {/* Right side: User menu */}
        <Box>
          <IconButton
            onClick={handleOpenMenu}
            size="small"
            sx={{ p: 0 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={profileImage || ""}
                  sx={{
                    bgcolor: "grey.100",
                    border: "1px solid",
                    borderColor: "rgba(0, 0, 0, 0.08)",
                    color: "text.primary",
                    width: 32,
                    height: 32,
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                {hasUnreadTimeline && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -2,
                      right: -2,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#dc2626",
                      border: "2px solid white",
                    }}
                  />
                )}
              </Box>

              {!isMobile && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 750,
                    color: "text.primary",
                    opacity: 0.85,
                  }}
                >
                  {username || "Account"}
                </Typography>
              )}
            </Stack>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled>
              {username || "Signed in"}
            </MenuItem>

            <Divider />

            <MenuItem onClick={() => handleNavigate(DESTINATIONS.today.path)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <BarChartIcon fontSize="small" />
              </ListItemIcon>
              {DESTINATIONS.today.label}
            </MenuItem>

            <MenuItem onClick={() => handleNavigate(DESTINATIONS.connections.path)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PersonAddAltOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {DESTINATIONS.connections.label}
                {hasIncomingRequests && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#dc2626",
                    }}
                  />
                )}
              </Box>
            </MenuItem>

            <MenuItem onClick={() => handleTrustBudNavigate(DESTINATIONS.sprint.path)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <GroupsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {DESTINATIONS.sprint.label}
            </MenuItem>

            <MenuItem onClick={() => handleNavigate(DESTINATIONS.updates.path)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </ListItemIcon>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {DESTINATIONS.updates.label}
                {hasUnreadTimeline && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#dc2626",
                    }}
                  />
                )}
              </Box>
            </MenuItem>

            <MenuItem onClick={() => handleNavigate(DESTINATIONS.plan.path)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <TuneOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {DESTINATIONS.plan.label}
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleInvite}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <MailOutlineIcon fontSize="small" />
              </ListItemIcon>
              Invite someone
            </MenuItem>

            <MenuItem onClick={() => handleNavigate("/account-settings")}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SettingsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Account Settings
            </MenuItem>

            <MenuItem onClick={() => handleNavigate("/trustbud-plus")}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WorkspacePremiumOutlinedIcon fontSize="small" />
              </ListItemIcon>
              TrustBud Plus
            </MenuItem>

            <MenuItem onClick={() => handleNavigate("/how-trustbud-works")}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <HelpOutlineIcon fontSize="small" />
              </ListItemIcon>
              How TrustBud Works
            </MenuItem>

            <MenuItem onClick={handleOpenFeedback}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </ListItemIcon>
              Send Feedback
            </MenuItem>

            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <LogoutOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
    <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} surface="top_bar" />
    </>
  );
}

export default TopBar;
